import { useSyncExternalStore } from 'react';

/**
 * THE ONLY PLACE JS KNOWS A BREAKPOINT. `tokens.component.css` carries the same four queries as
 * token blocks; `layoutTiers.test.ts` reads that file and fails if either side drifts. Spec:
 * docs/superpowers/specs/2026-09-14-studio-desktop-layout-design.md §3.
 *
 * `short` (R1) is keyed on HEIGHT, deliberately: a landscape phone is wide enough for the rail and
 * a right panel but has no height for a dock or a left panel. It is still additive — it only ever
 * applies on top of `tablet`.
 */
export type LayoutTier = 'phone' | 'tablet' | 'short' | 'studio2' | 'studio3';

// raw-ok: architectural breakpoints, THE ONLY PLACE JS defines them; mirrored in tokens.component.css; layoutTiers.test.ts holds both equal
export const LAYOUT_QUERIES = {
  tablet: '(min-width: 768px)', // raw-ok: architectural breakpoint
  studio2: '(min-width: 1024px)', // raw-ok: architectural breakpoint
  studio3: '(min-width: 1280px)', // raw-ok: architectural breakpoint
  short: '(min-width: 768px) and (max-height: 519px)', // raw-ok: architectural breakpoint
} as const;

export type TierMatches = { tablet: boolean; short: boolean; studio2: boolean; studio3: boolean };

export function tierFor(m: TierMatches): LayoutTier {
  if (!m.tablet) return 'phone';
  if (m.short) return 'short';
  if (m.studio3) return 'studio3';
  if (m.studio2) return 'studio2';
  return 'tablet';
}

/** Item-grid columns per tier — the JS twin of the `--grid-cols` token (virtualised grids chunk
 *  rows in JS, so CSS alone cannot carry this). */
export const GRID_COLS: Record<LayoutTier, number> = { phone: 4, tablet: 6, short: 6, studio2: 6, studio3: 8 };

/** True wherever a Studio right slot exists (the tiers on which an Aside is a panel). */
export function isStudio(tier: LayoutTier): boolean {
  return tier === 'short' || tier === 'studio2' || tier === 'studio3';
}

const KEYS = ['tablet', 'short', 'studio2', 'studio3'] as const;

function read(): LayoutTier {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'phone';
  const m = {} as TierMatches;
  for (const k of KEYS) m[k] = window.matchMedia(LAYOUT_QUERIES[k]).matches;
  return tierFor(m);
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const lists = KEYS.map((k) => window.matchMedia(LAYOUT_QUERIES[k]));
  for (const l of lists) l.addEventListener('change', onChange);
  return () => { for (const l of lists) l.removeEventListener('change', onChange); };
}

/** The current tier, live. `useSyncExternalStore` so a resize never tears between two components
 *  in one commit, and so nothing here mutates shared state during render (CLAUDE.md Lesson 1). */
export function useLayoutTier(): LayoutTier {
  return useSyncExternalStore(subscribe, read, () => 'phone');
}
